import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldOff } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import useKioskData from '@/hooks/useKioskData';
import {
  effectiveRigStatus,
  bookingReference,
  dispatchSession,
  markSanitised,
} from '@/lib/kiosk';
import KioskHeader from '@/components/kiosk/KioskHeader';
import BookingCheckInList from '@/components/kiosk/BookingCheckInList';
import DispatchConsole from '@/components/kiosk/DispatchConsole';
import FleetGrid from '@/components/kiosk/FleetGrid';
import QrScannerModal from '@/components/kiosk/QrScannerModal';
import LogFaultModal from '@/components/kiosk/LogFaultModal';

const VENUE = 'Bromborough Unit 9';

export default function VenueKiosk() {
  const { user, isLoadingAuth, navigateToLogin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isAdmin = user?.role === 'admin';
  const { bookings, rigs, feeds, experiences, loading, refreshing, refresh, tick } = useKioskData(isAdmin);

  const [search, setSearch] = useState('');
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [selectedRigId, setSelectedRigId] = useState('');
  const [ffb, setFfb] = useState(80);
  const [pedal, setPedal] = useState('medium_load_cell');
  const [dispatching, setDispatching] = useState(false);
  const [busyRigId, setBusyRigId] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [faultRig, setFaultRig] = useState(null);

  // Access guard — admins only.
  useEffect(() => {
    if (isLoadingAuth) return;
    if (!user) {
      navigateToLogin();
    } else if (user.role !== 'admin') {
      navigate('/', { replace: true });
    }
  }, [isLoadingAuth, user, navigateToLogin, navigate]);

  // Every physical bay, ordered — drives both the fleet grid and the Target Rig picker.
  const bays = useMemo(
    () =>
      rigs
        .filter((r) => r.bay_number)
        .sort((a, b) => String(a.bay_number).localeCompare(String(b.bay_number))),
    [rigs]
  );

  // A bay whose rig is reporting a running sim is IN USE, so it is withheld from
  // the dispatch picker rather than offered as free.
  const readyRigs = useMemo(
    () => bays.filter((r) => effectiveRigStatus(r, feeds) === 'READY'),
    [bays, feeds]
  );

  useEffect(() => {
    if (selectedRigId && !readyRigs.some((r) => r.id === selectedRigId)) setSelectedRigId('');
  }, [readyRigs, selectedRigId]);

  const selectedBooking = bookings.find((b) => b.id === selectedBookingId) || null;

  const visibleBookings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bookings;
    return bookings.filter((b) =>
      [b.customer_name, b.customer_email, bookingReference(b)].some((v) =>
        String(v || '').toLowerCase().includes(q)
      )
    );
  }, [bookings, search]);

  const handleScanResult = (decoded) => {
    setScanOpen(false);
    const raw = String(decoded || '').trim();
    const match = bookings.find((b) => b.id === raw || bookingReference(b) === raw.toUpperCase());
    if (!match) {
      toast({ title: 'No matching booking', description: `Scanned: ${raw}`, variant: 'destructive' });
      return;
    }
    setSelectedBookingId(match.id);
    setTimeout(
      () => document.getElementById(`kiosk-booking-${match.id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
      100
    );
  };

  const handleDispatch = async () => {
    const rig = readyRigs.find((r) => r.id === selectedRigId);
    if (!selectedBooking || !rig) return;
    setDispatching(true);
    try {
      await dispatchSession({
        booking: selectedBooking,
        rig,
        ffbGainPct: ffb,
        pedalProfile: pedal,
        dispatchedBy: user?.email,
      });
      toast({ title: 'Session dispatched', description: `${selectedBooking.customer_name} → ${rig.bay_number}` });
      setSelectedBookingId(null);
      setSelectedRigId('');
      await refresh();
    } catch (e) {
      toast({ title: 'Dispatch failed', description: e.message, variant: 'destructive' });
    } finally {
      setDispatching(false);
    }
  };

  const handleSanitise = async (rig) => {
    setBusyRigId(rig.id);
    try {
      await markSanitised(rig);
      await refresh();
    } catch (e) {
      toast({ title: 'Could not update the bay', description: e.message, variant: 'destructive' });
    } finally {
      setBusyRigId(null);
    }
  };

  if (isLoadingAuth || loading) {
    return (
      <div className="fixed inset-0 bg-[#0d0d0f] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="fixed inset-0 bg-[#0d0d0f] flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#0d0d0f] text-white overflow-hidden">
      <KioskHeader user={user} venue={VENUE} onRefresh={refresh} refreshing={refreshing} />

      <div className="flex-1 min-h-0 grid grid-cols-[45fr_55fr]">
        <BookingCheckInList
          bookings={visibleBookings}
          experiences={experiences}
          search={search}
          onSearch={setSearch}
          selectedBookingId={selectedBookingId}
          onSelect={setSelectedBookingId}
          onScan={() => setScanOpen(true)}
        />

        <div className="flex flex-col min-h-0">
          <div className="p-5 pb-0">
            <DispatchConsole
              booking={selectedBooking}
              bays={bays}
              feeds={feeds}
              selectedRigId={selectedRigId}
              onRigChange={setSelectedRigId}
              ffb={ffb}
              onFfb={setFfb}
              pedal={pedal}
              onPedal={setPedal}
              onDispatch={handleDispatch}
              dispatching={dispatching}
            />
          </div>
          <FleetGrid
            bays={bays}
            feeds={feeds}
            tick={tick}
            busyRigId={busyRigId}
            onSanitise={handleSanitise}
            onLogFault={setFaultRig}
          />
        </div>
      </div>

      {scanOpen && <QrScannerModal onResult={handleScanResult} onClose={() => setScanOpen(false)} />}
      {faultRig && (
        <LogFaultModal
          rig={faultRig}
          user={user}
          onClose={() => setFaultRig(null)}
          onSaved={() => {
            toast({ title: 'Fault recorded', description: `${faultRig.bay_number} — logged for maintenance` });
            setFaultRig(null);
          }}
        />
      )}
    </div>
  );
}